import { InvisibleButton } from "../Buttons/Buttons";
import type {
  AdvancedFilterCard,
  Filters,
  NumericFilters,
  ProductFiltersMainSchema,
  SupportedLanguage,
  TagClassificationConfig,
} from "../../types/types";
import { FiltersWrapper } from "../../layout/publicPageLayout";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import useSWR from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import { endpoints } from "../../endpoints";
import {
  getCustomLabel,
  toTitleCase,
  useMediaQueries,
  useStoreState,
} from "../../util/util";
import noop from "lodash/noop";
import { useCookies } from "react-cookie";
import { useHistory, useLocation } from "react-router-dom";
import { CaretDownIcon, XIcon } from "../Icons/Icons";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import { getProductStatusText } from "../Status/Status";
import type { ProductStatusType } from "../../types/types.PIM";
import { useMdmEntityDisplayNames } from "../../util/mdmEntityDisplayNames";
import { screenSize } from "../../theme";
import { loadFilters, saveFilters } from "../../util/filterStateManager";
import { EntityType } from "../../types/types.MDM";
import {
  decodeNumericFilterCriterion,
  facetOptionValueMatches,
  formatNumericFilterCriterion,
} from "./filterutils";
import {
  ADVANCED_FILTERS_KEY,
  decodeAdvancedFilterCards,
  encodeAdvancedFilterCards,
  formatAdvancedFilterCardLabel,
  useAdvancedFilterCollectionInstances,
} from "../AdvancedFilters/advancedFiltersUtils";

interface ICustomFiltersWrapper {
  showAll: boolean;
}

const CustomFiltersWrapper = styled(FiltersWrapper)<ICustomFiltersWrapper>`
  background-image: none;
  background-position: left 6px;
  padding-left: 0;
  overflow: auto;
  max-height: 180px;
  background-size: 18px;
  margin: 0;
  transition: all 200ms ease;
  width: auto !important;
  @media ${screenSize.small} {
    max-height: 120px;
  }
`;

const FiltersListArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: no-wrap;
  justify-content: flex-start;
  align-items: flex-start;
`;
const EmptyFiltersPlaceholder = styled.div`
  width: 100%;
`;

const FilterItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  border: ${({ theme }) => theme.resolve("border_sm")} solid
    ${({ theme }) => theme.resolve("color_border_light")};
  font-size: ${({ theme }) => theme.fontSizes.small};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};
  align-items: center;
  margin: 0 ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_xs")} 0;
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  button {
    padding: ${({ theme }) => theme.resolve("spacing_xxs")};
    margin-left: ${({ theme }) => theme.resolve("spacing_xxs")};
    display: flex;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  margin-top: 3px;
`;

type SelectedFilterChip =
  | { kind: "filter"; type: string; label: string; originalValue: string }
  | { kind: "advanced"; label: string; cardIndex: number; testId: string };

type FilterAreaProps = {
  resetOffset?: React.Dispatch<React.SetStateAction<number>>;
  clearFilter: () => void;
  filtersList: string[];
  isPublic: boolean;
  entityType?: EntityType;
  customLabels?: TagClassificationConfig[];
  onApplyAdvancedFilters?: (cards: AdvancedFilterCard[]) => void;
};

export function SelectedFiltersArea({
  resetOffset = noop,
  clearFilter,
  filtersList,
  entityType,
  customLabels = [],
  onApplyAdvancedFilters,
}: FilterAreaProps) {
  const location = useLocation();
  const { pathname } = location;

  const {
    storefront_id,
    storefront_metadata: { enable_advanced_filters },
    slug: tenantSlug,
  } = useStoreState();
  const [showAllFilters, setShowAllFilters] = useState(false);
  const history = useHistory();
  const theme = useTheme();
  const { t } = useTranslation();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const [cookies] = useCookies([`preferred-language-${tenantSlug}`]);
  const preferredLanguage: SupportedLanguage | undefined = cookies[
    `preferred-language-${tenantSlug}`
  ] as SupportedLanguage;

  const wrapperRef = useRef(document.createElement("div"));
  const { isLargeScreen } = useMediaQueries();

  // Cards left in the URL while Advanced Filters are unavailable are ignored by the backend, so don't show them as chips.
  const advancedFilterCards = enable_advanced_filters
    ? decodeAdvancedFilterCards(
        loadFilters(location)[ADVANCED_FILTERS_KEY]?.[0]
      ) ?? []
    : [];
  const {
    instances: advancedFilterInstances,
    isLoading: isLoadingAdvancedFilterInstances,
  } = useAdvancedFilterCollectionInstances(storefront_id, {
    enabled: advancedFilterCards.length > 0,
  });

  const getAdvancedFilterChips = (): SelectedFilterChip[] => {
    if (isLoadingAdvancedFilterInstances) return [];
    return advancedFilterCards.map((card, cardIndex) => {
      const instance = advancedFilterInstances.find(
        (candidate) => candidate.id === card.collection_instance_id
      );
      return {
        kind: "advanced",
        // The instance can be gone (deleted/invalidated) while its card is
        // still applied; keep a chip so the card can still be cleared.
        label: instance
          ? formatAdvancedFilterCardLabel(card, instance, t)
          : card.key_attribute_value,
        cardIndex,
        testId: `${card.collection_instance_id}-${card.key_attribute_value}`,
      };
    });
  };

  const getFiltersFromURL = (
    facetData: Filters = {},
    numericFiltersData: NumericFilters = {}
  ) => {
    const allFilters = loadFilters(location);
    const parms: SelectedFilterChip[] = [];

    Object.entries(allFilters).forEach(([key, values]) => {
      if (filtersList?.indexOf(key) > -1 && Array.isArray(values)) {
        // AP-1016 (Advanced Filters, Part 1): a numeric filter's applied
        // value is a single encoded operator+value(s) criterion, not a
        // chip — render it as "<Attribute>: <criterion>" instead of
        // resolving it against the facet's option list. Numeric filter
        // keys live in the separate `numeric_filters` map, never in
        // `facetData`/`filters` (docs/plans/NUMERIC_FILTERS_FE_SPEC.md §3).
        if (key in numericFiltersData) {
          const attributeLabel = getCustomLabel({
            filter_type: key,
            tag_classification_configs: customLabels,
            preferred_language: preferredLanguage,
          });
          values.forEach((value) => {
            const criterion = decodeNumericFilterCriterion(value);
            if (!criterion) return;
            parms.push({
              kind: "filter",
              type: key,
              label: `${attributeLabel}: ${formatNumericFilterCriterion(
                criterion,
                t
              )}`,
              originalValue: value,
            });
          });
          return;
        }

        values.forEach((value) => {
          // For id-keyed facets (e.g. `user`), the URL stores the id; show the
          // matching facet entry's name instead of the raw UUID.
          const matchedById = (facetData[key] ?? []).find((entry) =>
            facetOptionValueMatches(entry, value)
          );
          // Translate status values to display text
          const displayLabel =
            key === "status"
              ? getProductStatusText(value as ProductStatusType, t)
              : key === "template"
              ? getTemplateDisplayName({
                  template_name: matchedById?.name ?? value,
                  template_type: entityType,
                })
              : matchedById?.name ?? value;
          parms.push({
            kind: "filter",
            type: key,
            label:
              displayLabel === t("Unassigned")
                ? `${t("Unassigned")} (${toTitleCase(key.replace(/_/g, " "))})`
                : displayLabel,
            originalValue: value, // Keep original value for URL operations
          });
        });
      }
    });

    return [...parms, ...getAdvancedFilterChips()];
  };

  const [query] = useQueryParams({
    active_tag_view: StringParam,
    q: StringParam,
  });

  const {
    data: filters,
    error: filtersError,
    mutate: mutateFilters,
  } = useSWR<ProductFiltersMainSchema, Error>(
    entityType
      ? endpoints.v2_storefronts_id_mdm_entity_type_filters(
          storefront_id,
          entityType
        )
      : endpoints.v2_storefronts_id_products_filters(storefront_id)
  );

  const handleFilterClick = (filter: SelectedFilterChip) => {
    // Load current filters
    const currentFilters = loadFilters(location);

    // Remove the clicked filter value
    const updatedFilters: { [key: string]: string[] } = { ...currentFilters };

    if (filter.kind === "advanced") {
      const remainingCards = advancedFilterCards.filter(
        (_, index) => index !== filter.cardIndex
      );
      if (remainingCards.length > 0) {
        updatedFilters[ADVANCED_FILTERS_KEY] = [
          encodeAdvancedFilterCards(remainingCards),
        ];
      } else {
        delete updatedFilters[ADVANCED_FILTERS_KEY];
      }
      // Go through the same apply handler as the Advanced Filters modal, so
      // removing the last chip behaves exactly like its "Clear all".
      onApplyAdvancedFilters?.(remainingCards);
    } else {
      if (updatedFilters[filter.type]) {
        updatedFilters[filter.type] = updatedFilters[filter.type].filter(
          (v) => v !== filter.originalValue
        );

        // If no values left for this filter type, remove the key entirely
        if (updatedFilters[filter.type].length === 0) {
          delete updatedFilters[filter.type];
        }
      }

      // Add active_tag_view for UI state
      updatedFilters.active_tag_view = [filter.type];
    }

    if (filter.kind !== "advanced" || !onApplyAdvancedFilters) {
      saveFilters(updatedFilters, history, pathname, {
        q: query?.q || undefined,
      });
      resetOffset(0);
    }

    // If that was the last individually-cleared filter, reset the active
    // preset too — otherwise it stays selected (and "update preset" stays
    // active) despite none of its filters remaining applied.
    const hasRemainingFilters = [...filtersList, ADVANCED_FILTERS_KEY].some(
      (key) => (updatedFilters[key]?.length ?? 0) > 0
    );
    if (!hasRemainingFilters) {
      clearFilter();
    }
  };

  const handleClearFilters = () => {
    // Clear all filters, keeping only non-filter params
    const clearedFilters: { [key: string]: string[] } = {};

    if (query.active_tag_view) {
      clearedFilters.active_tag_view = [query.active_tag_view];
    }

    saveFilters(clearedFilters, history, pathname, {
      q: query?.q || undefined,
    });

    resetOffset(0);
    clearFilter();
  };

  useEffect(() => {
    mutateFilters();
  }, [query, mutateFilters]);

  if (filtersError) {
    // Search still works if filters don't load.
    // This maintains the layout of the page in case of error.
    return <EmptyFiltersPlaceholder />;
  } else if (filters?.filters) {
    // Handle case that happened once on staging where all filters were empty arrays.
    // This might theoretically be possible if a tenant is activated and no filters
    // are added.
    const filter_labels = getFiltersFromURL(
      filters.filters,
      filters.numeric_filters
    );
    if (filter_labels.length === 0) return null;
    else {
      return (
        <FiltersContainer>
          <CustomFiltersWrapper showAll={showAllFilters}>
            <FiltersListArea ref={wrapperRef}>
              {filter_labels?.map(
                (filter, index) =>
                  (showAllFilters ||
                    (!showAllFilters && index < (isLargeScreen ? 3 : 10))) && (
                    <FilterItemWrapper key={index}>
                      <span>{filter.label}</span>
                      <InvisibleButton
                        data-testid={
                          filter.kind === "advanced"
                            ? `remove-selected-advanced-filter-${filter.testId}`
                            : `remove-selected-filter-${filter.type}-${filter.originalValue}`
                        }
                        onClick={() => handleFilterClick(filter)}
                      >
                        <XIcon width={16} height={16} />
                      </InvisibleButton>
                    </FilterItemWrapper>
                  )
              )}
              {!showAllFilters &&
                filter_labels?.length > (isLargeScreen ? 3 : 10) && (
                  <span
                    onClick={() => setShowAllFilters(true)}
                    style={{
                      fontSize: theme.fontSizes.small,
                      cursor: "pointer",
                      marginTop: "7px",
                      marginLeft: "5px",
                    }}
                  >
                    and {filter_labels?.length - (isLargeScreen ? 3 : 10)}{" "}
                    more...
                  </span>
                )}
            </FiltersListArea>
          </CustomFiltersWrapper>
          <IconsContainer>
            {filter_labels?.length > (isLargeScreen ? 3 : 10) && (
              <InvisibleButton
                onClick={() => setShowAllFilters((prev) => !prev)}
                style={{
                  transform: showAllFilters ? "rotate(180deg)" : "none",
                }}
              >
                <CaretDownIcon width={16} height={16} />
              </InvisibleButton>
            )}

            {filter_labels?.[0]?.label && (
              <InvisibleButton
                onClick={() => {
                  handleClearFilters();
                }}
              >
                <XIcon
                  width={19}
                  height={19}
                  fill={theme.destructiveTextColor}
                />
              </InvisibleButton>
            )}
          </IconsContainer>
        </FiltersContainer>
      );
    }
  } else return null;
}
